import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { zoneLabel } from "@/lib/zones";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Siren, BedDouble, ArrowRight, ChevronDown } from "lucide-react";

const T = {
  en: { title: "Recurring pain", days: "days in a row", body: "Same zone flagged 3+ consecutive days. Offload it before it becomes an injury.", protocol: "Rest protocol", loading: "Building protocol…", restDays: "rest days", do: "Do", avoid: "Avoid", back: "Return when", doctor: "See a doctor if", medical: "Open in Medical" },
  fr: { title: "Douleur récurrente", days: "jours d'affilée", body: "Même zone signalée 3 jours consécutifs ou plus. Décharge-la avant que ça devienne une blessure.", protocol: "Protocole de repos", loading: "Création du protocole…", restDays: "jours de repos", do: "À faire", avoid: "À éviter", back: "Reprise quand", doctor: "Consulte si", medical: "Ouvrir dans Médical" },
};

const List = ({ title, items, color }) => items?.length > 0 && (
  <div>
    <div className="text-[10px] font-mono-tech uppercase tracking-wider mb-1" style={{ color }}>{title}</div>
    <ul className="space-y-0.5 text-xs text-slate-300">{items.map((s, i) => <li key={i} className="pl-2 border-l" style={{ borderColor: `${color}66` }}>{s}</li>)}</ul>
  </div>
);

// Reusable banner: fetches alerts itself (player) or renders `alerts` passed by a club view.
export const PainAlerts = ({ alerts: given, compact = false, readOnly = false }) => {
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const nav = useNavigate();
  const { can } = useAuth();
  const canProtocol = can("protocol");
  const [alerts, setAlerts] = useState(given || []);
  const [open, setOpen] = useState(null);
  const [protocols, setProtocols] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => { if (given) setAlerts(given); }, [given]);
  useEffect(() => { if (!given) api.get("/daily/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, [given]);

  const loadProtocol = async (a) => {
    if (!canProtocol) return nav("/pricing");
    if (open === a.zone) return setOpen(null);
    setOpen(a.zone);
    if (protocols[a.zone]) return;
    setBusy(a.zone);
    try {
      const r = await api.post("/daily/alerts/protocol", { zone: a.zone, streak: a.streak, lang });
      setProtocols((p) => ({ ...p, [a.zone]: r.data.protocol }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setOpen(null); }
    finally { setBusy(null); }
  };

  if (!alerts.length) return null;
  return (
    <div className="space-y-2" data-testid="pain-alerts">
      {alerts.map((a) => {
        const p = protocols[a.zone];
        return (
          <div key={a.zone} className="rounded-xl border border-[#FF2E54]/60 bg-[#FF2E54]/10 p-4" data-testid={`pain-alert-${a.zone}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Siren className="w-5 h-5 text-[#FF2E54] shrink-0 animate-pulse" />
              <div className="flex-1 min-w-[180px]">
                <div className="font-display font-bold uppercase text-sm">
                  {t.title} · <span className="text-[#FF2E54]">{zoneLabel(a.zone, lang)}</span> · <span data-testid={`pain-alert-streak-${a.zone}`}>{a.streak}</span> {t.days}
                </div>
                {!compact && <div className="text-xs text-slate-300 mt-0.5">{t.body}</div>}
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button size="sm" onClick={() => loadProtocol(a)} data-testid={`pain-alert-protocol-${a.zone}`} className="bg-[#FF2E54] hover:bg-[#d92046] text-white font-bold text-xs uppercase">
                    <BedDouble className="w-3 h-3 mr-1" />{t.protocol}<ChevronDown className={`w-3 h-3 ml-1 transition-transform ${open === a.zone ? "rotate-180" : ""}`} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => nav(`/medical?zone=${a.zone}`)} data-testid={`pain-alert-medical-${a.zone}`} className="text-slate-300 text-xs">
                    {t.medical}<ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </div>
            {open === a.zone && (
              <div className="mt-3 rounded-lg bg-[#090C10] border border-[#1E2638] p-4" data-testid={`pain-protocol-${a.zone}`}>
                {busy === a.zone && !p ? <div className="text-xs text-slate-400 animate-pulse">{t.loading}</div> : p && (
                  <>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-display font-bold text-base">{p.headline}</div>
                      {p.rest_days != null && <div className="px-3 py-1 rounded-full bg-[#FF2E54]/20 text-[#FF2E54] text-xs font-mono-tech font-bold">{p.rest_days} {t.restDays}</div>}
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <List title={t.do} items={p.do} color="#00F076" />
                      <List title={t.avoid} items={p.avoid} color="#FF2E54" />
                      <List title={t.back} items={p.return_criteria} color="#FFB800" />
                      <List title={t.doctor} items={p.see_doctor_if} color="#a78bfa" />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
